import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useMonth } from "../context/MonthContext";
import { useMonthlyIncomes } from "../hooks/useMonthlyIncomes";
import { incomeApi, ApiIncome } from "../services/api";
import { formatCurrency } from "../utils/helpers";

interface IncomeEntryProps {
  refreshTrigger?: number;
  onIncomeChanged: () => void;
}

const getDateInputValue = (date: string) => new Date(date).toISOString().split("T")[0];

const IncomeEntry: React.FC<IncomeEntryProps> = ({ refreshTrigger, onIncomeChanged }) => {
  const { user } = useAuth();
  const { selectedMonth } = useMonth();
  const { incomes, totalIncome, loading, refreshIncomes } = useMonthlyIncomes(refreshTrigger);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingIncome, setEditingIncome] = useState<ApiIncome | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    description: "",
    category: "工资",
    amount: "",
  });

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split("T")[0],
      description: "",
      category: "工资",
      amount: "",
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  React.useEffect(() => {
    resetForm();
  }, [selectedMonth]);

  // Add income
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user?.group_id || !user.user_id) return;

    setIsSubmitting(true);
    try {
      const payload = {
        date: formData.date,
        description: formData.description.trim(),
        category: formData.category.trim(),
        amount: parseFloat(formData.amount),
        receiver_id: user.user_id,
        group_id: user.group_id,
      };

      await incomeApi.createIncome(payload);
      resetForm();
      setShowAddModal(false);
      await refreshIncomes();
      onIncomeChanged();
    } catch (error) {
      console.error("Failed to save income:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteIncome = async (income: ApiIncome) => {
    setIsSubmitting(true);
    try {
      await incomeApi.deleteIncome(income.income_id);
      await refreshIncomes();
      onIncomeChanged();
    } catch (error) {
      console.error("Failed to delete income:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit income
  const handleEditSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingIncome || !user?.group_id) return;
    setIsSubmitting(true);
    try {
      await incomeApi.updateIncome(editingIncome.income_id, {
        date: formData.date,
        description: formData.description.trim(),
        category: formData.category.trim(),
        amount: parseFloat(formData.amount),
        receiver_id: user.user_id,
        group_id: user.group_id,
      });
      setEditingIncome(null);
      resetForm();
      await refreshIncomes();
      onIncomeChanged();
    } catch (error) {
      console.error("Failed to update income:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (income: ApiIncome) => {
    setFormData({
      date: getDateInputValue(income.date),
      description: income.description,
      category: income.category,
      amount: income.amount.toString(),
    });
    setEditingIncome(income);
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const closeModal = () => {
    setEditingIncome(null);
    setShowAddModal(false);
    resetForm();
  };

  const isModalOpen = showAddModal || !!editingIncome;
  const modalTitle = editingIncome ? "编辑收入" : "添加收入";
  const modalSubtitle = editingIncome ? "修改收入记录" : "收入会进入月度汇总，并用于计算净结余。";
  const modalSubmitText = editingIncome ? "保存收入" : "添加收入";
  const handleModalSubmit = editingIncome ? handleEditSave : handleSubmit;

  return (
    <section className="mb-12">
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">收入记录</h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">合计 {formatCurrency(totalIncome)}</span>
            <button
              type="button"
              onClick={openAddModal}
              className="p-1.5 rounded-lg text-slate-600 hover:bg-gray-100 hover:text-slate-800 transition-colors"
              title="添加收入"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-700 mx-auto mb-2"></div>
            <p className="text-gray-600">加载收入记录中...</p>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[160px]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    日期
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    描述
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    类别
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    金额
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {incomes.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-6 text-center text-sm text-gray-500">
                      当前月份没有收入记录
                    </td>
                  </tr>
                )}
                {incomes.map((income) => (
                  <tr key={income.income_id}>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                      {new Date(income.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900 max-w-48 truncate">
                      {income.description}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">{income.category}</td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-green-700 text-right font-medium">
                      {formatCurrency(income.amount)}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-center">
                      <div className="flex justify-center space-x-2">
                        <button
                          type="button"
                          onClick={() => openEditModal(income)}
                          className="text-indigo-600 hover:text-indigo-900 transition-colors"
                          title="编辑"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteIncome(income)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="删除"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit Income Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-800">{modalTitle}</h2>
                <p className="text-gray-500 text-sm mt-1">{modalSubtitle}</p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleModalSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">日期</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">金额 (¥)</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  onWheel={(e) => e.currentTarget.blur()}
                  min="0.01"
                  step="0.01"
                  placeholder="12000.00"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">描述</label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="例如：工资"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">类别</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  placeholder="例如：工资、奖金、副业"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm p-2"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-slate-700 text-white font-bold py-2 px-4 rounded-lg hover:bg-slate-800 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "保存中..." : modalSubmitText}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default IncomeEntry;
